import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { AddressCollectionModel, AddressModel } from "@store/Services/CheckoutService";
import autocomplete from "@store/Store/Pages/CheckoutShipping/Handlers/AddressAutocomplete";
import isCountryCodeUS from "@store/utilities/isCountryCodeUS";
import debounce from "lodash/debounce";
import React, { FC, useCallback, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface AutocompleteAddressFormFieldProps {
    extendedStyles?: AutocompleteAddressFormFieldStyles;
    disabled?: boolean;
    required?: boolean;
    maxLength?: number;
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
    name?: string;
    label?: React.ReactNode;
    value?: string | string[] | number;
    error?: React.ReactNode;
    onSelect: (address: AddressModel) => void;
    country: string;
    forceToUSAutocomplete: boolean;
}

const mapDispatchToProps = {
    autocomplete,
};

type Props = AutocompleteAddressFormFieldProps & ResolveThunks<typeof mapDispatchToProps>;

export interface AutocompleteAddressFormFieldStyles {
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    wrapper?: InjectableCss;
}

export const autocompleteAddressTextfield: AutocompleteAddressFormFieldStyles = {
    wrapper: {
        css: css`
            font-style: normal;
        `,
    },
};

export const AutocompleteAddressTextfieldWrapper = getStyledWrapper("address");

const AutocompleteAddressTextField: FC<Props> = ({
    extendedStyles,
    autocomplete,
    label,
    value,
    disabled,
    required,
    error,
    onSelect,
    country,
    forceToUSAutocomplete,
    onChange,
}) => {

    const [styles] = React.useState(() => mergeToNew(autocompleteAddressTextfield, extendedStyles));

    const [options, setOptions] = useState<OptionObject[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [addresses, setAddresses] = useState<AddressModel[] | undefined>(undefined);
    const [userInput, setUserInput] = useState("");
    const [address, setAddress] = useState<AddressModel | undefined>(undefined);
    const SMARTYENTRYNUMBER = 1;
    const useUSAutocomplete = (isCountryCodeUS(country) || forceToUSAutocomplete);

    useEffect(() => {
        setUserInput(`${value}`);
    }, [value]);

    useEffect(() => {
        setAddresses(undefined);
        setOptions([]);
        setAddress(undefined);
        setUserInput(`${value}`);
    }, [country]);

    const handleError = () => {
        setIsLoading(false);
        setAddresses(undefined);
        setOptions([]);
    };

    const handleSelectAddress = (address: AddressModel | undefined) => {
        setAddress(address);
        if (address) {
            setUserInput(address.address1 || "");
            const newOptions = (isCountryCodeUS(address.countryCode) || forceToUSAutocomplete) ?
                options.filter(option => option.optionValue !== address.address1) :
                options.filter(option => option.optionText !== address.fullAddress);
            setOptions(newOptions);

            onSelect?.(address);
        }
    };

    const debouncedHandleSearch = useCallback(debounce((inputValue: string, countryParam: string, isCountryCodeUSOrForcedToUS: boolean) => {
        const formatOptionText = isCountryCodeUSOrForcedToUS
            ? (address: AddressModel) => `${address.address1}, ${address.city}, ${address.stateCode}, ${address.postalCode}`
            : (address: AddressModel) => `${address.address1}`;
        commonSearchLogic(inputValue, countryParam, formatOptionText, isCountryCodeUSOrForcedToUS);
    }, 300), []);

    const commonSearchLogic = (inputValue: string, countryParam: string, formatOptionText: (address: AddressModel) => string, isCountryCodeUSOrForcedToUS: boolean) => {
        setIsLoading(true);

        autocomplete({
            searchTerm: inputValue,
            country: countryParam,
            forceToUSAutocomplete: isCountryCodeUSOrForcedToUS,
            onSuccess: (result) => {
                setIsLoading(false);
                if (result.addresses) {
                    setAddresses(result.addresses);
                    const newOptions = result.addresses.map((address) => ({
                        optionText: formatOptionText(address),
                        optionValue: `${address.address1}`,
                      }));
                    addOrUpdateOptions(newOptions);
                } else {
                    setAddresses(undefined);
                    setOptions([]);
                }
            },
            onError: handleError
        });
    }
    
    const addOrUpdateOptions = (newOptions: OptionObject[]) => {
        setOptions(currentOptions => {
            const uniqueNewOptionsMap = new Map(newOptions.map(option => [option.optionValue, option]));
            const uniqueNewOptions = Array.from(uniqueNewOptionsMap.values());
            const existingOptionValues = new Map(currentOptions.map(option => [option.optionValue, true]));
            const filteredNewOptions = uniqueNewOptions.filter(option => !existingOptionValues.has(option.optionValue));
            return filteredNewOptions;
          });
      };


    const cleanInternationalAddress = (city: string | undefined, stateCode: string | undefined, postalCode: string | undefined, address: AddressModel) => {
        const cityRegex = new RegExp(`\\b${city}\\b`, 'g');
        const stateCodeRegex = new RegExp(`\\b${stateCode}\\b`, 'g');
        const postalCodeRegex = new RegExp(`\\b${postalCode}\\b`, 'g');

        let cleanedAddress = address.address1?.replace(cityRegex, '')
            .replace(stateCodeRegex, '')
            .replace(postalCodeRegex, '');

        cleanedAddress = cleanedAddress?.replace(/,\s*$/, '').trim() || "";

        return cleanedAddress;
    };


    const debouncedInternationalAddressSearch = debounce((countryParam: string, addressIdParam: string | undefined, entriesParam: number | undefined) => {
        setIsLoading(true);
        autocomplete({
            country: countryParam,
            addressId: addressIdParam,
            entries: entriesParam,
            onSuccess: (result: AddressCollectionModel) => {
                setIsLoading(false);
                if (result.addresses) {
                    setAddresses(result.addresses);
                    const selectedAddress = result.addresses[0];
                    if (selectedAddress !== null) {
                        setAddress(selectedAddress);
                        onSelect?.(selectedAddress);
                        setOptions([]);
                    }
                } else {
                    setAddresses(undefined);
                    setOptions([]);
                }
            },
            onError: handleError
        });
    }, 300);

    const handleAddressChange: TextFieldProps["onChange"] = (event) => {
        const inputValue = event.target.value;
        setUserInput(inputValue);

        if (onChange) {
            const syntheticEvent = Object.create(event);
            const address1Name = "address1"
            syntheticEvent.target = {
                ...event.target,
                name: address1Name, 
                value: inputValue,
            };
            syntheticEvent.currentTarget = {
                ...event.currentTarget,
                name: address1Name,
                value: inputValue,
            };
            onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
        }

        if (inputValue === "") {
            setIsLoading(false);
            setOptions([]);
            setAddresses(undefined);
            setAddress(undefined);
            return;
        }

        if (inputValue !== "" && country) {
            debouncedHandleSearch(inputValue, country, useUSAutocomplete);
        }
    };

    const selectAddressHandler = (address1?: string) => {
        const addressSelected = !useUSAutocomplete ? addresses?.filter(addressSelected => addressSelected.fullAddress === address1)[0] : addresses?.filter(addressSelected => addressSelected.address1 === address1)[0];

        if (!addressSelected) { return handleError(); }

        if (useUSAutocomplete) {
            if (address?.address1 === addressSelected.address1) {
                return;
            }
        } else if (address?.fullAddress === addressSelected.fullAddress) {
            return;
        }


        if (!useUSAutocomplete && addressSelected.addressId) {
            if (Number(addressSelected.entries) > SMARTYENTRYNUMBER) {
                autocomplete({
                    searchTerm: addressSelected?.address1,
                    country,
                    addressId: addressSelected?.addressId,
                    entries: addressSelected?.entries,
                    onSuccess: (result) => {
                        if (result.addresses) {
                            const { addressId, countryCode, entries } = result.addresses[0];
                            autocomplete({
                                country: countryCode || "",
                                addressId,
                                entries,
                                onSuccess: (result) => {
                                    if (result.addresses) {
                                        const { postalCode, city, stateCode, address1 } = result.addresses[0];
                                        const cleanedAddress = cleanInternationalAddress(city, stateCode, postalCode, addressSelected)
                                        if (address1?.includes(cleanedAddress)) {
                                            addressSelected.address1 = cleanedAddress;
                                        }
                                        addressSelected.postalCode = postalCode;
                                        addressSelected.city = city;
                                        addressSelected.stateCode = stateCode;
                                        addressSelected.countryCode = country;

                                        handleSelectAddress(addressSelected)
                                    }
                                },
                                onError: handleError,
                            });
                        }
                    },
                    onError: handleError,
                });
            } else {
                debouncedInternationalAddressSearch(country, addressSelected.addressId, addressSelected.entries);
            }
        } else if (useUSAutocomplete) {
            handleSelectAddress(addressSelected);
        } else {
            handleError();
        }
    };
    
    return (
        <>
            <DynamicDropdown
                {...styles.searchDynamicDropdown}
                label={label}
                search={userInput}
                disabled={disabled}
                required={required}
                error={error}
                onSelectionChange={selectAddressHandler}
                onInputChange={handleAddressChange}
                selected={address?.address1}
                isLoading={isLoading}
                options={options}
                data-test-selector="autocompleteAddressSelector"
                filterOption={() => true}
            />
        </>
    );
}

export default connect(null, mapDispatchToProps)(AutocompleteAddressTextField); // Poolcorp Custom Code






